import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmployeeCalendarProps } from '~/components/EmployeeCalendar';
import {
  EmployeeCalendarEvent,
  EmployeeCalendarEventColors,
  EmployeeCalendarEventLegend,
} from '~/components/EmployeeCalendar/data';
import { useCompanyData } from '~/data/company';
import { People } from '~/services/Http';

import {
  AbsenceLegendKeys,
  SYSTEM_PUBLIC_HOLIDAY_EVENT,
  absenceEventColors,
} from './data';

type PropsType = {
  events: EmployeeCalendarEvent[];
  eventColorLegends: EmployeeCalendarEventLegend[];
  getAbsenceLegend: (
    event: EmployeeCalendarEvent,
  ) => EmployeeCalendarEventLegend | undefined;
};

type UseEmployeeAbsenceCalendarConfigOutput = Pick<
  EmployeeCalendarProps,
  'events' | 'getEventColors' | 'getEventContent' | 'legends'
>;

export const useEmployeeAbsenceCalendarConfig = (
  props: PropsType,
): UseEmployeeAbsenceCalendarConfigOutput => {
  const { events, eventColorLegends, getAbsenceLegend } = props;

  const theme = useTheme();

  const { t } = useTranslation();

  const { companyData } = useCompanyData();
  const leaveTypes = useMemo(() => {
    const {
      fComPublicLeaveTypes,
      fComSickLeaveTypes,
      fComVacationLeaveTypes,
      fComVacationLeaveExtraTypes,
      fComVacationBankLeaveTypes1,
      fComVacationBankLeaveTypes2,
      fComVacationBankLeaveTypes3,
      fComYearlyVacationBalanceTypes,
    } = companyData.config.fComLeaveTypes;

    return {
      publicLeave: fComPublicLeaveTypes,
      sickLeave: fComSickLeaveTypes,
      vacationLeave: fComVacationLeaveTypes,
      extraVacationLeave: fComVacationLeaveExtraTypes,
      bank1Leave: fComVacationBankLeaveTypes1,
      bank2Leave: fComVacationBankLeaveTypes2,
      bank3Leave: fComVacationBankLeaveTypes3,
      yearlyVacationLeave: fComYearlyVacationBalanceTypes,
    };
  }, [companyData]);

  const isOfLeaveType = (absenceReason: string, leaveTypes: string[]) => {
    return leaveTypes.includes(absenceReason);
  };

  const getAbsenceColorKey = useCallback(
    (absenceReason: string) => {
      if (absenceReason === SYSTEM_PUBLIC_HOLIDAY_EVENT) {
        return AbsenceLegendKeys.PublicHoliday;
      }

      const {
        sickLeave,
        vacationLeave,
        extraVacationLeave,
        bank1Leave,
        bank2Leave,
        bank3Leave,
        yearlyVacationLeave,
      } = leaveTypes;

      const isSickLeave = isOfLeaveType(absenceReason, sickLeave);
      if (isSickLeave) {
        return AbsenceLegendKeys.SickLeave;
      }

      const isVacation = isOfLeaveType(absenceReason, vacationLeave);
      const isExtra = isOfLeaveType(absenceReason, extraVacationLeave);
      const isBank1 = isOfLeaveType(absenceReason, bank1Leave);
      const isBank2 = isOfLeaveType(absenceReason, bank2Leave);
      const isBank3 = isOfLeaveType(absenceReason, bank3Leave);
      const isYearly = isOfLeaveType(absenceReason, yearlyVacationLeave);
      if (isVacation || isExtra || isBank1 || isBank2 || isBank3 || isYearly) {
        return AbsenceLegendKeys.Holiday;
      }

      return AbsenceLegendKeys.Other;
    },
    [leaveTypes],
  );

  const { legends } = useMemo(() => {
    const { PUBLIC_HOLIDAY } = absenceEventColors;

    const symbolLegends: EmployeeCalendarEventLegend[] = [
      {
        key: 'notApproved',
        label: t('notApproved'),
        content: '?',
      },
      {
        key: 'longTermAbsence',
        label: t('longTermAbsence'),
        content: 'L',
      },
      {
        key: 'noEndDate',
        label: t('noEndDate'),
        content: '★',
      },
      {
        key: 'publicHoliday',
        label: t('publicHoliday'),
        colors: {
          color: theme.palette.common.white,
          backgroundColor: PUBLIC_HOLIDAY,
        },
        content: 'PH',
      },
    ];

    return {
      legends: symbolLegends.concat(eventColorLegends),
    };
  }, [t, theme, eventColorLegends]);

  const getLegendContent = useCallback(
    (key: string): string => {
      return legends.find((legend) => legend.key === key)?.content ?? '';
    },
    [legends],
  );

  const getAbsenceColor = useCallback(
    (absenceReason: string) => {
      const colorKey = getAbsenceColorKey(absenceReason);

      return legends.find(({ key }) => key === colorKey)?.colors;
    },
    [getAbsenceColorKey, legends],
  );

  const getEventColors = useCallback(
    (event: EmployeeCalendarEvent): EmployeeCalendarEventColors | undefined => {
      return (
        getAbsenceLegend(event)?.colors ?? getAbsenceColor(event.eventCode)
      );
    },
    [getAbsenceColor, getAbsenceLegend],
  );

  const getEventContent = useCallback(
    (event: EmployeeCalendarEvent) => {
      const { status, endDate, eventCategory } = event;

      const symbols = {
        notApproved: getLegendContent('notApproved'),
        longTermAbsence: getLegendContent('longTermAbsence'),
        noEndDate: getLegendContent('noEndDate'),
        publicHoliday: getLegendContent('publicHoliday'),
      };

      if (eventCategory === SYSTEM_PUBLIC_HOLIDAY_EVENT) {
        return symbols.publicHoliday;
      }

      let contentSymbol = '';
      if (status !== People.AbsenceStatusEnum.APPROVED) {
        contentSymbol += symbols.notApproved;
      }
      if (eventCategory === People.AbsenceTypeEnum.LONG) {
        contentSymbol += symbols.longTermAbsence;
      }

      if (typeof endDate === 'undefined') {
        if (
          contentSymbol === `${symbols.notApproved}${symbols.longTermAbsence}`
        ) {
          contentSymbol = `${symbols.notApproved}${symbols.noEndDate}`;
        } else {
          contentSymbol += symbols.noEndDate;
        }
      }

      return contentSymbol;
    },
    [getLegendContent],
  );

  return {
    events,
    getEventContent,
    getEventColors,
    legends,
  };
};
